@if (isOpen) {
  <!-- Backdrop -->
  <div class="activity-backdrop" @fadeIn (click)="onBackdropClick()"></div>
  <!-- Panel -->
  <div class="activity-panel" @slideDown>
    <!-- Header -->
    <div class="activity-header">
      <div class="activity-header-inner">
        <div class="activity-header-top">
          <div class="activity-header-left">
            <div class="activity-header-icon">
              <mat-icon class="header-icon">history</mat-icon>
            </div>
            <div>
              <h3 class="activity-title">Activity</h3>
              <p class="activity-subtitle">{{ totalCount }} item{{ totalCount !== 1 ? 's' : '' }} in history</p>
            </div>
          </div>
          <button class="activity-close-btn" (click)="onClose.emit()" aria-label="Close activity panel">
            <mat-icon>close</mat-icon>
          </button>
        </div>
        <!-- Search -->
        <div class="activity-search-wrap">
          <mat-icon class="search-icon">search</mat-icon>
          <input
            type="text"
            placeholder="Search history..."
            [ngModel]="searchQuery"
            (ngModelChange)="onSearchChange($event)"
            class="activity-search-input"
            />
          @if (searchQuery) {
            <button class="search-clear-btn" (click)="clearSearch()">
              <mat-icon class="search-clear-icon">close</mat-icon>
            </button>
          }
        </div>
      </div>
    </div>
    <!-- Filter Bar -->
    <div class="activity-filter-bar">
      @for (chip of filterChips; track chip) {
        <button
          class="filter-chip"
          [class.active]="activeFilter === chip.value"
          (click)="onFilterChange(chip.value)"
          >
          {{ chip.label }}
        </button>
      }
    </div>
    <!-- Activity List -->
    <div class="activity-list">
      <!-- Loading State: initial/search fetch in flight. Shown instead of the
           empty state so a slow request doesn't briefly read as "No activity
           yet" before the real items arrive. -->
      @if (isLoading) {
        <div class="activity-empty">
          <div class="empty-icon-wrap loading-icon-wrap">
            <mat-icon class="empty-icon loading-icon spinning">refresh</mat-icon>
          </div>
          <h4 class="empty-title">Loading activity...</h4>
        </div>
      } @else if (groupedActivities.length === 0) {
        <!-- Empty State -->
        <div class="activity-empty">
          <div class="empty-icon-wrap">
            <mat-icon class="empty-icon">{{ searchQuery ? 'search' : 'history' }}</mat-icon>
          </div>
          <h4 class="empty-title">{{ searchQuery ? 'No matching activity' : 'No activity yet' }}</h4>
          <p class="empty-text">
            {{ searchQuery ? 'Nothing found for "' + searchQuery + '"' : 'Your browsing history will appear here.' }}
          </p>
        </div>
      }
      <!-- Grouped Items: gated on !isLoading too — groupedActivities still holds
           the PREVIOUS fetch's items while a new one is in flight (this component
           stays alive across panel open/close), so without this guard the stale
           list rendered underneath the loading spinner at the same time. -->
      @if (!isLoading) {
      @for (group of groupedActivities; track trackByGroup($index, group)) {
        <div>
          <!-- Group Header -->
          <div class="group-header">
            <span class="group-label">{{ group.label }}</span>
          </div>
          <!-- Group Items -->
          <div class="group-items">
            @for (item of group.items; track trackByActivity(i, item); let i = $index) {
              <div
                class="activity-item"
                [class.pinned]="item.pinned"
                @slideIn
                >
                <div
                  class="item-row"
                  [class.clickable]="isClickable(item)"
                  [attr.role]="isClickable(item) ? 'button' : null"
                  [attr.tabindex]="isClickable(item) ? 0 : null"
                  [attr.aria-label]="isClickable(item) ? 'Open ' + item.title : null"
                  (click)="onActivityClick(item)"
                  (keydown)="onActivityKeydown($event, item)"
                  >
                  <!-- Icon Badge -->
                  <div
                    class="item-icon-badge"
                    [style.color]="getIconColor(item.type)"
                    [style.background-color]="getBgColor(item.type)"
                    [style.border-color]="getBorderColor(item.type)"
                    >
                    <mat-icon class="item-icon">{{ item.iconType }}</mat-icon>
                  </div>
                  <!-- Content -->
                  <div class="item-content">
                    <div class="item-top-row">
                      <div class="item-title-wrap">
                        <div class="item-title-line">
                          @if (item.pinned) {
                            <mat-icon class="pin-indicator">push_pin</mat-icon>
                          }
                          <h4 class="item-title" [matTooltip]="item.title">{{ item.title }}</h4>
                        </div>
                        <div class="item-meta">
                          <span
                            class="type-pill"
                            [style.color]="getIconColor(item.type)"
                            [style.background-color]="getBgColor(item.type)"
                            [style.border-color]="getBorderColor(item.type)"
                            >
                            {{ getTypeLabel(item.type) }}
                          </span>
                          <span class="item-subtitle" [innerHTML]="item.subtitle"></span>
                        </div>
                      </div>
                      <!-- Timestamp + Hover Actions -->
                      <div class="item-actions-wrap">
                        <span class="item-timestamp">{{ formatRelativeTime(item) }}</span>
                        <div class="item-hover-actions">
                          <button
                            class="hover-action-btn"
                            [class.pin-active]="item.pinned"
                            (click)="onTogglePin(item.id); $event.stopPropagation()"
                            [matTooltip]="item.pinned ? 'Unpin' : 'Pin'"
                            >
                            <mat-icon class="hover-action-icon">push_pin</mat-icon>
                          </button>
                          <button
                            class="hover-action-btn delete-btn"
                            (click)="onDelete(item.id); $event.stopPropagation()"
                            matTooltip="Remove"
                            >
                            <mat-icon class="hover-action-icon">delete_outline</mat-icon>
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            }
          </div>
        </div>
      }
      }
      <!-- Load More: same stale-state guard as the grouped list above. -->
      @if (!isLoading && hasMore) {
        <div class="load-more-wrap">
          <button class="load-more-btn" (click)="loadMore()" [disabled]="isLoadingMore">
            <mat-icon class="load-more-icon" [class.spinning]="isLoadingMore">refresh</mat-icon>
            {{ isLoadingMore ? 'Loading...' : 'Load More' }}
          </button>
        </div>
      }
    </div>
    <!-- Footer -->
    @if (totalCount > 0) {
      <div class="activity-footer">
        <div class="footer-left">
          @if (pinnedCount > 0) {
            <span class="pinned-badge">
              <mat-icon class="pinned-badge-icon">push_pin</mat-icon>
              {{ pinnedCount }} pinned
            </span>
          }
        </div>
        <button
          class="clear-btn"
          (click)="onClearAll()"
          >
          Clear History
        </button>
      </div>
    }
  </div>
}
